import React from 'react';
import { teamMembers, localize } from '../mock';
import { Award, Target, Users, Zap, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';
import SEO from '../components/SEO';
import { SectionHero, SectionCTA, SectionHeading } from '../components/Sections';

const valueIcons = [Award, Zap, Users, CheckCircle];

const ValueCard = ({ value, icon: Icon }) => (
  <Card className="border-0 hover:shadow-xl transition-all duration-500 group">
    <CardContent className="p-6 text-center">
      <div className="bg-orange-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
        <Icon className="h-8 w-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
      <p className="text-gray-600">{value.description}</p>
    </CardContent>
  </Card>
);

const TeamMemberCard = ({ member, lang }) => (
  <Card className="border-0 hover:shadow-2xl transition-all duration-500 overflow-hidden group">
    <CardContent className="p-8">
      <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        <span className="text-3xl font-bold text-white">{member.name.charAt(0)}</span>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">{member.name}</h3>
      <p className="text-orange-500 font-semibold text-center mb-4">{localize(member, 'role', lang)}</p>
      <p className="text-gray-600 text-center">{localize(member, 'bio', lang)}</p>
    </CardContent>
  </Card>
);

const About = () => {
  const { lang } = useLanguage();
  const t = translations[lang].about;

  return (
    <div className="min-h-screen">
      <SEO path="/about" />
      <SectionHero title1={t.heroTitle1} titleAccent={t.heroTitleAccent} title2={t.heroTitle2} description={t.heroDescription} />

      {/* Mission & Vision */}
      <section data-testid="mission-vision" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow duration-500">
              <CardContent className="p-8">
                <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.missionTitle}</h2>
                <p className="text-lg text-gray-600">{t.missionDescription}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow duration-500">
              <CardContent className="p-8">
                <div className="bg-orange-100 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="h-8 w-8 text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.visionTitle}</h2>
                <p className="text-lg text-gray-600">{t.visionDescription}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section data-testid="core-values" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.valuesTitle} titleAccent={t.valuesTitleAccent} subtitle={t.valuesSubtitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.values.map((value, index) => (
              <ValueCard key={value.title} value={value} icon={valueIcons[index]} />
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="team-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.teamTitle} titleAccent={t.teamTitleAccent} subtitle={t.teamSubtitle} />
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <TeamMemberCard key={member.id} member={member} lang={lang} />
            ))}
          </div>
        </div>
      </section>

      <SectionCTA title={t.ctaTitle} description={t.ctaDescription} buttonText={t.getInTouch} buttonLink="/contact" testId="about-get-in-touch-btn" />
    </div>
  );
};

export default About;
